import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, CheckCircle2, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

interface DiscoveredOrganization {
    organization_id: string;
    organization_name: string;
    organization_logo_url?: string; // Optional logo URL
    membership: {
        type: string;
        details: any;
    };
    member_id: string;
}

interface OrganizationSelectionProps {
    discoveredOrganizations: DiscoveredOrganization[];
    intermediateSessionToken: string;
    onSelectOrganization: (organizationId: string, intermediateToken: string) => Promise<void>;
}

/**
 * OrganizationSelection - Shows list of organizations for user to select
 * Auto-selects if only one organization
 * Shows "Recently used" badge for last used organization
 */
const OrganizationSelection: React.FC<OrganizationSelectionProps> = ({
    discoveredOrganizations,
    intermediateSessionToken,
    onSelectOrganization
}) => {
    const { t } = useTranslation();
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Get last used organization from localStorage
    const lastUsedOrgId = localStorage.getItem('lastUsedOrganizationId');

    useEffect(() => {
        // Auto-select if only one organization
        if (discoveredOrganizations.length === 1) {
            const orgId = discoveredOrganizations[0].organization_id;
            handleSelect(orgId);
        }
    }, [discoveredOrganizations]);

    useEffect(() => {
        console.log('🎨 OrganizationSelection: Rendering with organizations:',
            discoveredOrganizations.map(org => ({
                id: org.organization_id,
                name: org.organization_name
            }))
        );
    }, [discoveredOrganizations]);

    const handleSelect = async (organizationId: string) => {
        setSelectedOrgId(organizationId);
        setIsLoading(true);

        try {
            // Store as last used
            localStorage.setItem('lastUsedOrganizationId', organizationId);

            await onSelectOrganization(organizationId, intermediateSessionToken);
        } catch (error) {
            console.error('Failed to select organization:', error);
            setIsLoading(false);
        }
    };

    // If auto-selecting single org, show loading
    if (discoveredOrganizations.length === 1) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mb-4"></div>
                <p className="text-muted-foreground">
                    {t('auth.organizationSelection.joiningOrganization')}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
            <div className="w-full max-w-2xl space-y-6">
                {/* Header */}
                <div className="text-center space-y-2">
                    <h1 className="text-3xl font-bold">
                        {t('auth.organizationSelection.title')}
                    </h1>
                    <p className="text-muted-foreground">
                        {t('auth.organizationSelection.description')}
                    </p>
                </div>

                {/* Organization List */}
                <div className="space-y-3">
                    {discoveredOrganizations.map((org) => {
                        const isRecentlyUsed = org.organization_id === lastUsedOrgId;
                        const role = org.membership?.type || 'member';

                        return (
                            <Card
                                key={org.organization_id}
                                className={`cursor-pointer transition-all hover:shadow-md ${selectedOrgId === org.organization_id ? 'border-gold bg-gold/5' : ''
                                    }`}
                                onClick={() => !isLoading && handleSelect(org.organization_id)}
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        {/* Organization Info */}
                                        <div className="flex items-start gap-4 flex-1">
                                            {/* Organization Logo or Icon */}
                                            {org.organization_logo_url ? (
                                                <div className="rounded-lg bg-gold/10 flex items-center justify-center w-16 h-16 overflow-hidden">
                                                    <img
                                                        src={org.organization_logo_url}
                                                        alt={org.organization_name}
                                                        className="w-full h-full object-contain"
                                                        onError={(e) => {
                                                            // Fallback to Building2 icon if logo fails to load
                                                            const parent = e.currentTarget.parentElement;
                                                            if (parent) {
                                                                parent.innerHTML = '<svg class="h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>';
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="p-3 rounded-lg bg-gold/10">
                                                    <Building2 className="h-6 w-6 text-gold" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-semibold text-lg truncate">
                                                        {(console.log('🏷️ Rendering org name:', org.organization_name), org.organization_name)}
                                                    </h3>
                                                    {isRecentlyUsed && (
                                                        <Badge variant="outline" className="gap-1">
                                                            <Clock className="h-3 w-3" />
                                                            {t('auth.organizationSelection.recentlyUsed')}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <Badge variant="secondary" className="text-xs">
                                                        {role === 'admin'
                                                            ? t('organization.members.roles.admin')
                                                            : t('organization.members.roles.member')
                                                        }
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Select Button */}
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelect(org.organization_id);
                                            }}
                                            disabled={isLoading}
                                            className="ml-4"
                                        >
                                            {isLoading && selectedOrgId === org.organization_id ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                    {t('auth.organizationSelection.joining')}
                                                </>
                                            ) : (
                                                <>
                                                    {t('auth.organizationSelection.select')}
                                                    <CheckCircle2 className="ml-2 h-4 w-4" />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default OrganizationSelection;
